/**
 * InstallationOrchestrationService
 * 
 * Purpose: Represent the deterministic installation workflow as a discrete, callable function.
 * This service is the architectural boundary between "configuration preparation" and "installation execution".
 * 
 * Key Principles:
 * - Stateless: All state is passed as parameters, no internal state
 * - Deterministic: Same input always produces same output
 * - Discrete: Installation is a function call, not a state transition
 * 
 * Responsibilities:
 * - Generate installation plan from requirements and selected modules
 * - Calculate installation order based on dependencies
 * - Prepare deployment configuration
 * - Trigger installation execution via API
 * 
 * Modularity: This service is called by the UI layer via WorkflowAdapter,
 * ensuring clean separation between UI events and backend installation workflow.
 */

export interface InstallationConfiguration {
    // Installation metadata
    sessionId: string;
    timestamp: Date;

    // Module installation details
    modules: ModuleInstallationItem[];
    installationOrder: string[]; // Technical names in dependency order

    // Server configuration
    serverRequirements: {
        cpu: number;
        ram: number;
        storage: number;
        dropletSize: string;
        region: string;
    };

    // Deployment artifacts
    deploymentScript: string;
    configFiles: {
        odooConf: string;
        dockerCompose: string;
        nginxConf: string;
    };

    // Resource estimates
    estimatedDuration: number; // seconds
    estimatedCost: number; // monthly USD
}

export interface ModuleInstallationItem {
    technicalName: string;
    name: string;
    version: string;
    dependencies: string[];
    installationStep: number;
    estimatedInstallTime: number; // seconds
}

export interface InstallationResult {
    success: boolean;
    installationId: string;
    status: 'pending' | 'in-progress' | 'completed' | 'failed';
    message: string;
    startedAt?: Date;
    completedAt?: Date;
    logs?: string[];
    error?: string;
}

class InstallationOrchestrationService {

    /**
     * Generate installation plan from requirements and selected modules.
     * This is the primary method for preparing installation configuration.
     * 
     * @param sessionId - User session identifier
     * @param selectedModules - Technical names of selected modules
     * @param requirements - User requirements submission
     * @returns Installation configuration ready for execution
     */
    async generateInstallationPlan(
        sessionId: string,
        selectedModules: string[],
        requirements: any
    ): Promise<InstallationConfiguration> {

        // This will call the backend /api/orchestrate endpoint
        // Backend performs the actual deterministic workflow

        const response = await fetch('/api/orchestrate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                sessionId,
                modules: selectedModules,
                requirements
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to generate installation plan: ${response.statusText}`);
        }

        const data = await response.json();

        // Transform backend response to InstallationConfiguration format
        return this.transformOrchestrationResponse(data, sessionId);
    }

    /**
     * Execute the installation workflow.
     * This is the discrete, deterministic function that triggers installation.
     * 
     * @param configuration - Installation configuration from generateInstallationPlan
     * @returns Installation result with tracking information
     */
    async executeInstallation(
        configuration: InstallationConfiguration
    ): Promise<InstallationResult> {

        // Call backend deployment endpoint
        const response = await fetch('/api/deploy/execute', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                sessionId: configuration.sessionId,
                configuration
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to execute installation: ${response.statusText}`);
        }

        const data = await response.json();

        return {
            success: data.success,
            installationId: data.data.installationId || data.data.deploymentId,
            status: data.data.status || 'pending',
            message: data.message || 'Installation started successfully',
            startedAt: new Date(),
            logs: data.data.logs || []
        };
    }

    /**
     * Check installation status (for progress tracking)
     * 
     * @param installationId - Installation identifier from executeInstallation
     * @returns Current installation status
     */
    async checkInstallationStatus(
        installationId: string
    ): Promise<InstallationResult> {

        const response = await fetch(`/api/deploy/status/${installationId}`);

        if (!response.ok) {
            throw new Error(`Failed to check installation status: ${response.statusText}`);
        }

        const data = await response.json();

        return {
            success: data.success,
            installationId,
            status: data.data.status,
            message: data.data.message || '',
            startedAt: data.data.startedAt ? new Date(data.data.startedAt) : undefined,
            completedAt: data.data.completedAt ? new Date(data.data.completedAt) : undefined,
            logs: data.data.logs || [],
            error: data.data.error
        };
    }

    /**
     * Transform backend orchestration response to InstallationConfiguration
     * 
     * @private
     */
    private transformOrchestrationResponse(
        data: any,
        sessionId: string
    ): InstallationConfiguration {

        const orchData = data.data || data;

        return {
            sessionId,
            timestamp: new Date(),

            modules: this.buildModuleInstallationItems(
                orchData.installationOrder || [],
                orchData.modules || []
            ),

            installationOrder: orchData.installationOrder || [],

            serverRequirements: {
                cpu: orchData.serverRequirements?.cpu || 2,
                ram: orchData.serverRequirements?.ram || 4096,
                storage: orchData.serverRequirements?.storage || 50000,
                dropletSize: orchData.serverRequirements?.dropletSize || 's-2vcpu-4gb',
                region: orchData.serverRequirements?.region || 'nyc3'
            },

            deploymentScript: orchData.deploymentScript || '',

            configFiles: {
                odooConf: orchData.configFiles?.odooConf || '',
                dockerCompose: orchData.configFiles?.dockerCompose || '',
                nginxConf: orchData.configFiles?.nginxConf || ''
            },

            estimatedDuration: this.calculateEstimatedDuration(orchData.installationOrder?.length || 0),
            estimatedCost: orchData.serverRequirements?.estimatedCost || 48.00
        };
    }

    /**
     * Build module installation items with dependency order
     * 
     * @private
     */
    private buildModuleInstallationItems(
        installationOrder: string[],
        modules: any[]
    ): ModuleInstallationItem[] {

        return installationOrder.map((technicalName, index) => {
            const module = modules.find((m: any) => m.technicalName === technicalName);

            return {
                technicalName,
                name: module?.name || technicalName,
                version: module?.version || '18.0',
                dependencies: module?.dependencies || [],
                installationStep: index + 1,
                estimatedInstallTime: 30 // seconds per module
            };
        });
    }

    /**
     * Calculate estimated installation duration
     * 
     * @private
     */
    private calculateEstimatedDuration(moduleCount: number): number {
        // Base time + time per module
        const baseTime = 180; // 3 minutes for server setup
        const perModuleTime = 30; // 30 seconds per module

        return baseTime + (moduleCount * perModuleTime);
    }
}

// Export singleton instance
export const installationOrchestrationService = new InstallationOrchestrationService();
